// Invoices.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./Invoices.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const STATUS_TABS = ["All", "Open", "Closed"];

const emptyData = {
  kpis: { totalBalance: 0, openInvoiceCount: 0 },
  rows: [],
  statementLinks: [],
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
};

const getStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "OPEN") return "open";
  if (s === "CLOSED") return "closed";
  return "neutral";
};

// Parse MM/DD/YYYY date string to a comparable date (or null)
const parseMDY = (str) => {
  if (!str) return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const d = new Date(
    Number(parts[2]),
    Number(parts[0]) - 1,
    Number(parts[1])
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

// Parse YYYY-MM-DD (native date input value) to a comparable date
const parseYMD = (str) => {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d;
};

const matchesSearch = (row, term) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return [
    row.invoiceNumber != null ? String(row.invoiceNumber) : null,
    row.description,
    row.status,
    row.type,
    row.poNumber,
    row.dueDate,
    row.invoiceAmount != null ? String(row.invoiceAmount) : null,
    row.balance != null ? String(row.balance) : null,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

const matchesDateRange = (row, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const due = parseMDY(row.dueDate);
  if (!due) return !fromDate && !toDate;
  if (fromDate && due < fromDate) return false;
  if (toDate && due > toDate) return false;
  return true;
};

/* -------------------------------------------------------------------------- */
/*                              Reusable UI pieces                            */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, isLoading }) => (
  <div className="invoices-kpi-card">
    <div className="invoices-kpi-card__label">{label}</div>
    <div className="invoices-kpi-card__value">
      {isLoading ? <span className="card-spinner" /> : value}
    </div>
  </div>
);

const SkeletonRows = ({ cols, count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const StatementLinksCard = ({ statementLinks }) => (
  <div className="invoices-statement-card">
    <div className="invoices-statement-card__head">
      <span className="invoices-statement-card__col-label">Name</span>
      <span className="invoices-statement-card__col-label">
        View or Pay Statement
      </span>
    </div>
    <div className="invoices-statement-card__body">
      {statementLinks.length > 0 ? (
        statementLinks.map((item, idx) => (
          <div key={idx} className="invoices-statement-row">
            <span className="invoices-statement-name">{item.customerName}</span>
            {item.statementLink ? (
              <a
                href={item.statementLink}
                target="_blank"
                rel="noopener noreferrer"
                className="invoices-statement-link"
              >
                Click Here
              </a>
            ) : (
              <span style={{ color: "#94a3b8", fontSize: 13 }}>—</span>
            )}
          </div>
        ))
      ) : (
        <div className="invoices-statement-empty">No statement links available.</div>
      )}
    </div>
  </div>
);

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";
  return (
    <div ref={wrapperRef} className="invoices-filter-field">
      <label className="invoices-filter-field__label">Company Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="invoices-select-btn"
      >
        <span className="invoices-select-btn__text">{buttonLabel}</span>
        <span className={`invoices-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="invoices-multiselect-menu">
          <div className="invoices-multiselect-list">
            <div
              className={`invoices-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((c) => (
              <div
                key={c.id}
                className={`invoices-checkbox-row${String(selectedCustomerId) === String(c.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(c.id)); setOpen(false); }}
              >
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({
  properties,
  selectedPropertyIds,
  setSelectedPropertyIds,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const selectedNames = properties
    .filter((p) => selectedPropertyIds.includes(String(p.id)))
    .map((p) => p.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "Select values";
    if (selectedPropertyIds.length === 1) return selectedNames[0] || "1 Property";
    return `${selectedPropertyIds.length} selected`;
  })();

  const toggle = (id) => {
    setSelectedPropertyIds((curr) => {
      const s = String(id);
      return curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s];
    });
  };

  return (
    <div ref={wrapperRef} className="invoices-filter-field">
      <label className="invoices-filter-field__label">Property Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={`invoices-select-btn ${open ? "is-open" : ""}`}
      >
        <span className="invoices-select-btn__text">{buttonLabel}</span>
        <span className={`invoices-select-btn__chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      {open && !disabled ? (
        <div className="invoices-multiselect-menu">
          <div className="invoices-multiselect-actions">
            <button
              type="button"
              className="invoices-pill-btn"
              onClick={() => setSelectedPropertyIds([])}
            >
              All
            </button>
            <button
              type="button"
              className="invoices-pill-btn"
              onClick={() =>
                setSelectedPropertyIds(properties.map((p) => String(p.id)))
              }
            >
              Select All
            </button>
          </div>
          <div className="invoices-multiselect-list">
            {properties.length ? (
              properties.map((p) => (
                <label key={p.id} className="invoices-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedPropertyIds.includes(String(p.id))}
                    onChange={() => toggle(p.id)}
                  />
                  <span>{p.name}</span>
                </label>
              ))
            ) : (
              <div className="invoices-multiselect-empty">
                No properties available.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DateDueFilter = ({ fromDate, toDate, setFromDate, setToDate }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const hasValue = fromDate || toDate;
  const label = (() => {
    if (fromDate && toDate) return `${fromDate} – ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    if (toDate) return `To ${toDate}`;
    return "Select date range";
  })();

  return (
    <div ref={wrapperRef} className="invoices-filter-field">
      <label className="invoices-filter-field__label">Date Due Filter</label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`invoices-select-btn ${hasValue ? "has-value" : ""}`}
      >
        <span className="invoices-date-icon">&#128197;</span>
        <span className="invoices-select-btn__text">{label}</span>
        <span className={`invoices-select-btn__chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      {open ? (
        <div className="invoices-date-menu">
          <div className="invoices-date-row">
            <label className="invoices-date-label">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="invoices-date-input"
            />
          </div>
          <div className="invoices-date-row">
            <label className="invoices-date-label">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="invoices-date-input"
            />
          </div>
          {hasValue ? (
            <button
              type="button"
              className="invoices-pill-btn"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                  Invoices                                  */
/* -------------------------------------------------------------------------- */

const Invoices = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [data, setData] = useState(emptyData);

  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Load filters
  useEffect(() => {
    const load = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setFilterError("No auth token found.");
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/invoices/invoices-filters`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: viewerEmail ? { email: viewerEmail } : undefined,
          }
        );

        const d = res?.data || {};
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setProperties(Array.isArray(d.properties) ? d.properties : []);
      } catch (err) {
        console.error("Failed to load invoices filters:", err);
        setFilterError("Failed to load filters.");
      } finally {
        setFiltersLoading(false);
      }
    };

    load();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;
    return properties.filter(
      (p) => String(p.customerId ?? "") === String(selectedCustomerId)
    );
  }, [properties, selectedCustomerId]);

  // Drop selected property IDs when switching customer
  useEffect(() => {
    const allowed = new Set(filteredProperties.map((p) => String(p.id)));
    setSelectedPropertyIds((curr) => curr.filter((id) => allowed.has(id)));
  }, [filteredProperties]);

  // Load data when filters change
  useEffect(() => {
    const load = async () => {
      if (filtersLoading || filterError) return;

      try {
        setDataLoading(true);
        setDataError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setDataError("No auth token found.");
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/invoices/invoices-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length
                ? { propertyIds: selectedPropertyIds.join(",") }
                : {}),
            },
          }
        );

        setData(res?.data || emptyData);
      } catch (err) {
        console.error("Failed to load invoices data:", err);
        setDataError("Failed to load invoices data.");
        setData(emptyData);
      } finally {
        setDataLoading(false);
      }
    };

    load();
  }, [
    filtersLoading,
    filterError,
    viewerEmail,
    selectedCustomerId,
    selectedPropertyIds,
  ]);

  const fromDateObj = useMemo(() => parseYMD(fromDate), [fromDate]);
  const toDateObj = useMemo(() => parseYMD(toDate), [toDate]);

  // Compute visible rows: tab filter → date filter → search
  const visibleRows = useMemo(() => {
    let rows = data.rows || [];

    if (activeTab !== "All") {
      rows = rows.filter(
        (r) =>
          String(r.status || "").toUpperCase() === activeTab.toUpperCase()
      );
    }

    rows = rows.filter((r) => matchesDateRange(r, fromDateObj, toDateObj));
    rows = rows.filter((r) => matchesSearch(r, searchTerm));

    return rows;
  }, [data.rows, activeTab, fromDateObj, toDateObj, searchTerm]);

  const handleClearSearch = () => {
    setSelectedCustomerId("");
    setSelectedPropertyIds([]);
    setActiveTab("All");
    setSearchTerm("");
    setFromDate("");
    setToDate("");
  };

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="invoices-page">
      <div className="invoices-shell">
        {/* ---- Filter bar ---- */}
        <section className="invoices-toolbar">
          <div className="invoices-toolbar__grid">
            {/* Company */}
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            {/* Property */}
            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            {/* Date due */}
            <DateDueFilter
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />

            {/* Search */}
            <div className="invoices-filter-field invoices-filter-field--search">
              <label className="invoices-filter-field__label">Search</label>
              <input
                type="text"
                placeholder=""
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="invoices-search-input"
              />
            </div>

            {/* Clear */}
            <div className="invoices-toolbar__clear">
              <button
                type="button"
                onClick={handleClearSearch}
                className="invoices-clear-btn"
              >
                Clear Search
              </button>
            </div>
          </div>
        </section>

        {/* ---- Feedback ---- */}

        {filterError ? (
          <div className="invoices-feedback invoices-feedback--error">
            {filterError}
          </div>
        ) : null}

        {dataError ? (
          <div className="invoices-feedback invoices-feedback--error">
            {dataError}
          </div>
        ) : null}

        {!filterError ? (
          <>
            {/* ---- KPI cards + statement links ---- */}
            <section className="invoices-top-grid">
              <KpiCard
                label="Total Balance"
                value={formatCurrency(data.kpis?.totalBalance ?? 0)}
                isLoading={isLoading}
              />
              <KpiCard
                label="Open Invoices"
                value={data.kpis?.openInvoiceCount ?? 0}
                isLoading={isLoading}
              />
              <StatementLinksCard
                statementLinks={data.statementLinks || []}
              />
            </section>

            {/* ---- Status tabs + table ---- */}
            <section className="invoices-table-card">
              <div className="invoices-table-card__header">
                <span className="invoices-table-card__title">Invoice Status</span>
              </div>

              <div className="invoices-tabs">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`invoices-tab ${activeTab === tab ? "is-active" : ""}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="invoices-table-wrap">
                {isLoading || visibleRows.length > 0 ? (
                  <table className="invoices-table">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Due Date</th>
                        <th>Type</th>
                        <th>PO Number</th>
                        <th>Invoice Amount</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={8} /> : visibleRows.map((row, idx) => (
                        <tr
                          key={row.invoiceNumber != null ? String(row.invoiceNumber) : `row-${idx}`}
                          className={idx % 2 === 0 ? "" : "is-alt"}
                        >
                          <td>
                            <div className="invoices-primary-cell">
                              {row.invoiceNumber == null ? (
                                "—"
                              ) : row.fileUrl ? (
                                <a
                                  href={row.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="invoices-primary-link"
                                >
                                  {row.invoiceNumber}
                                </a>
                              ) : (
                                row.invoiceNumber
                              )}
                            </div>
                          </td>
                          <td className="invoices-description-cell">
                            {row.description || "—"}
                          </td>
                          <td>
                            <span
                              className={`invoices-status-pill invoices-status-pill--${getStatusTone(
                                row.status
                              )}`}
                            >
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.dueDate || "—"}</td>
                          <td>{row.type || "—"}</td>
                          <td>{row.poNumber || "—"}</td>
                          <td>{formatCurrency(row.invoiceAmount)}</td>
                          <td>{formatCurrency(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="invoices-empty-state">No invoices found</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Invoices;
